import React, { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../lib/AuthContext';
import { 
  Users, Search, Phone, Mail, Building2, 
  MapPin, Calendar, ExternalLink, Loader2, PhoneCall, X, Briefcase,
  MessageSquare, ChevronRight
} from 'lucide-react';
import { format } from 'date-fns';

interface UnifiedApplicant {
  id: string;
  created_at: string;
  stage: string;
  candidate_id: string;
  recruiter_id: string;
  recruiter_name: string;
  recruiter_company: string;
  recruiter_phone: string;
  job_role: string;
  campaign_title: string;
  candidate: {
    id: string;
    name: string;
    phone: string;
    city: string;
    experience_years: number;
    experience_details: any[];
    education_details: any[];
    skills: string[];
    cv_video_url?: string;
    cv_video_recorded?: boolean;
    photo_url?: string;
  };
}

// ── CUSTOM MODAL COMPONENT ──
const CandidateProfileModal = ({ 
  applicant, 
  onClose 
}: { 
  applicant: UnifiedApplicant, 
  onClose: () => void 
}) => {
  const { candidate } = applicant;
  
  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 lg:p-8">
      <div className="absolute inset-0 bg-slate-950/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white w-full max-w-4xl max-h-[90vh] rounded-[2.5rem] shadow-2xl flex flex-col overflow-hidden animate-in zoom-in-95 duration-200">
        
        {/* Header */}
        <div className="p-6 lg:p-8 border-b border-slate-100 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-2xl bg-slate-900 flex items-center justify-center text-white text-2xl font-black shrink-0">
              {candidate.name?.[0] || '?'}
            </div>
            <div>
              <h2 className="text-2xl font-black text-slate-900 tracking-tight">{candidate.name}</h2>
              <p className="text-slate-500 flex items-center gap-2 text-sm">
                <MapPin size={14} /> {candidate.city} • <Calendar size={14} /> {candidate.experience_years} Years Experience
              </p>
            </div>
          </div>
          <button 
            onClick={onClose}
            className="w-10 h-10 rounded-full bg-slate-50 flex items-center justify-center text-slate-400 hover:text-slate-900 hover:bg-slate-100 transition-all"
          >
            <X size={20} />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-6 lg:p-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            
            <div className="lg:col-span-12 space-y-8">
              
              {/* VIDEO SECTION */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-3">
                  <h3 className="text-xs font-bold uppercase tracking-widest text-slate-400">CV Video</h3>
                  {candidate.cv_video_url ? (
                    <div className="aspect-video bg-slate-950 rounded-3xl overflow-hidden shadow-lg border-2 border-slate-900">
                      <video 
                        src={candidate.cv_video_url} 
                        controls 
                        className="w-full h-full object-contain"
                      />
                    </div>
                  ) : (
                    <div className="aspect-video bg-slate-50 border-2 border-dashed border-slate-200 rounded-3xl flex flex-col items-center justify-center text-slate-400 p-6 text-center">
                      <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center mb-3">
                        <Users size={24} />
                      </div>
                      <p className="text-xs font-bold">No CV Video Uploaded</p>
                    </div>
                  )}
                </div>

                <div className="space-y-3">
                  <h3 className="text-xs font-bold uppercase tracking-widest text-slate-400">Application Context</h3>
                  <div className="bg-slate-50 rounded-3xl p-6 border border-slate-100 space-y-4">
                    <div>
                      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Applying For</p>
                      <p className="text-lg font-black text-slate-900 leading-tight">{applicant.job_role}</p>
                      <p className="text-sm text-slate-500">{applicant.campaign_title}</p>
                    </div>
                    <div className="pt-4 border-t border-slate-200">
                      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Managed By Recruiter</p>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-white border border-slate-200 flex items-center justify-center text-slate-900 font-bold shadow-sm">
                          {applicant.recruiter_name?.[0]}
                        </div>
                        <div>
                          <p className="text-sm font-bold text-slate-900">{applicant.recruiter_name}</p>
                          <a href={`tel:${applicant.recruiter_phone}`} className="text-xs text-blue-600 font-bold hover:underline flex items-center gap-1">
                            <Phone size={10} /> {applicant.recruiter_phone}
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* DETAILS GRID */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* Experience */}
                <div className="space-y-4">
                  <h3 className="text-xs font-bold uppercase tracking-widest text-slate-400 flex items-center gap-2">
                    <Briefcase size={14} /> Experience History
                  </h3>
                  <div className="space-y-3">
                    {candidate.experience_details && candidate.experience_details.length > 0 ? (
                      candidate.experience_details.map((exp: any, idx: number) => (
                        <div key={idx} className="bg-white border border-slate-100 p-4 rounded-2xl shadow-sm">
                          <p className="font-bold text-slate-900">{exp.role || exp.title}</p>
                          <p className="text-sm text-slate-500">{exp.company}</p>
                          <p className="text-[10px] text-slate-400 mt-1 uppercase font-bold">{exp.duration}</p>
                        </div>
                      ))
                    ) : (
                      <p className="text-sm text-slate-500 italic">No detailed experience history available.</p>
                    )}
                  </div>
                </div>

                {/* Education */}
                <div className="space-y-4">
                  <h3 className="text-xs font-bold uppercase tracking-widest text-slate-400 flex items-center gap-2">
                    <Building2 size={14} /> Education
                  </h3>
                  <div className="space-y-3">
                    {candidate.education_details && candidate.education_details.length > 0 ? (
                      candidate.education_details.map((edu: any, idx: number) => (
                        <div key={idx} className="bg-white border border-slate-100 p-4 rounded-2xl shadow-sm">
                          <p className="font-bold text-slate-900">{edu.degree || edu.qualification}</p>
                          <p className="text-sm text-slate-500">{edu.institution || edu.school}</p>
                          <p className="text-[10px] text-slate-400 mt-1 uppercase font-bold">{edu.year}</p>
                        </div>
                      ))
                    ) : (
                      <p className="text-sm text-slate-500 italic">No detailed education history available.</p>
                    )}
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>
        
        {/* Footer Actions */}
        <div className="p-6 lg:px-8 border-t border-slate-100 shrink-0 flex items-center justify-between bg-slate-50/50">
           <a 
            href={`tel:${candidate.phone}`}
            className="flex-1 max-w-[300px] bg-slate-900 text-white px-8 py-4 rounded-2xl font-black text-center shadow-lg hover:shadow-slate-900/20 hover:-translate-y-0.5 transition-all flex items-center justify-center gap-3"
          >
            <PhoneCall size={20} />
            CALL CANDIDATE
          </a>
          <button 
            className="px-6 py-4 text-slate-500 font-bold hover:text-slate-900 transition-colors"
            onClick={onClose}
          >
            Go Back
          </button>
        </div>
      </div>
    </div>
  );
};

// ── BULK MESSAGE MODAL ──
const BulkMessageModal = ({ 
  selectedCount, 
  onClose, 
  onSend 
}: { 
  selectedCount: number, 
  onClose: () => void, 
  onSend: (message: string) => void 
}) => {
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-slate-950/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white w-full max-w-lg rounded-[2rem] shadow-2xl flex flex-col overflow-hidden animate-in zoom-in-95 duration-200 border border-slate-100">
        <div className="p-6 border-b border-slate-100 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-black text-slate-900">Bulk Message</h2>
            <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mt-1">Sending to {selectedCount} candidates</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-full transition-colors"><X size={20} /></button>
        </div>
        <div className="p-6 space-y-4">
          <p className="text-sm text-slate-600 bg-blue-50 p-4 rounded-2xl border border-blue-100">
            <strong>Note:</strong> Messages will be sent individually from the respective managed recruiters of these candidates.
          </p>
          <textarea 
            className="w-full bg-slate-50 border border-slate-200 rounded-2xl px-5 py-4 text-sm font-medium focus:outline-none focus:border-slate-900 transition-all min-h-[150px] resize-none"
            placeholder="Write your message here..."
            value={message}
            onChange={(e) => setMessage(e.target.value)}
          />
        </div>
        <div className="p-6 bg-slate-50 border-t border-slate-100 flex items-center justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm font-bold text-slate-500">Cancel</button>
          <button 
            disabled={!message.trim() || sending}
            onClick={async () => {
              setSending(true);
              await onSend(message);
              setSending(false);
            }}
            className="bg-slate-900 text-white px-8 py-3 rounded-2xl text-sm font-black shadow-lg hover:shadow-slate-900/20 disabled:opacity-50 disabled:translate-y-0 transition-all flex items-center gap-2"
          >
            {sending ? <Loader2 className="animate-spin" size={18} /> : <MessageSquare size={18} />}
            SEND BROADCAST
          </button>
        </div>
      </div>
    </div>
  );
};

export default function MasterApplicants() {
  const { user } = useAuth();
  const [applicants, setApplicants] = useState<UnifiedApplicant[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedApplicant, setSelectedApplicant] = useState<UnifiedApplicant | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isBulkModalOpen, setIsBulkModalOpen] = useState(false);

  useEffect(() => {
    if (user) fetchAllApplicants();
  }, [user]);

  const fetchAllApplicants = async () => {
    setLoading(true);
    try {
      const { data: managedRecruiters } = await supabase
        .from('recruiters')
        .select('id, name, company_name, phone')
        .eq('managed_by', user!.id);

      if (!managedRecruiters || managedRecruiters.length === 0) {
        setApplicants([]);
        return;
      }

      const recruiterIds = managedRecruiters.map(r => r.id);

      const { data: campaigns } = await supabase
        .from('campaigns')
        .select('id, title, recruiter_id')
        .in('recruiter_id', recruiterIds);

      if (!campaigns || campaigns.length === 0) {
        setApplicants([]);
        return;
      }

      const campaignIds = campaigns.map(c => c.id);

      const { data: jobs } = await supabase
        .from('jobs')
        .select('id, role, campaign_id')
        .in('campaign_id', campaignIds);

      if (!jobs || jobs.length === 0) {
        setApplicants([]);
        return;
      }

      const jobIds = jobs.map(j => j.id);

      const { data: apps } = await supabase
        .from('applications')
        .select(`
          id, 
          created_at, 
          stage, 
          job_id,
          candidate_id,
          candidate:candidates(*)
        `)
        .in('job_id', jobIds)
        .order('created_at', { ascending: false });

      if (!apps) {
        setApplicants([]);
        return;
      }

      const unified: UnifiedApplicant[] = apps.map(app => {
        const job = jobs.find(j => j.id === app.job_id);
        const campaign = campaigns.find(c => c.id === job?.campaign_id);
        const recruiter = managedRecruiters.find(r => r.id === campaign?.recruiter_id);

        return {
          id: app.id,
          created_at: app.created_at,
          stage: app.stage,
          candidate_id: app.candidate_id,
          recruiter_id: recruiter?.id || '',
          recruiter_name: recruiter?.name || 'Unknown',
          recruiter_company: recruiter?.company_name || 'Unknown',
          recruiter_phone: recruiter?.phone || 'Unknown',
          job_role: job?.role || 'Unknown Job',
          campaign_title: campaign?.title || 'Unknown Campaign',
          candidate: app.candidate as any
        };
      });

      setApplicants(unified);
    } catch (err) {
      console.error('Error fetching master applicants:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleBulkSend = async (message: string) => {
    try {
      const selectedApps = applicants.filter(a => selectedIds.has(a.id));
      
      const promises = selectedApps.map(app => {
        return supabase.from('messages').insert({
          application_id: app.id,
          sender_id: app.recruiter_id,
          content: message,
          metadata: { is_bulk: true, master_sent_at: new Date().toISOString() }
        });
      });

      await Promise.all(promises);
      
      const notifPromises = selectedApps.map(app => {
        return supabase.from('notifications').insert({
          user_id: app.candidate_id,
          type: 'campaign_message',
          title: 'New Message',
          body: `Recruiter ${app.recruiter_name} sent you a message.`,
          data: { applicationId: app.id }
        });
      });

      await Promise.all(notifPromises);

      alert(`Successfully sent broadcast to ${selectedApps.length} candidates!`);
      setIsBulkModalOpen(false);
      setSelectedIds(new Set());
    } catch (err) {
      console.error('Error sending bulk messages:', err);
      alert('Failed to send some messages. Please try again.');
    }
  };

  const toggleSelection = (id: string) => {
    const next = new Set(selectedIds);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelectedIds(next);
  };

  const filteredApplicants = applicants.filter(app => {
    const search = searchTerm.toLowerCase();
    return (
      app.candidate?.name?.toLowerCase().includes(search) ||
      app.candidate?.phone?.includes(searchTerm) ||
      app.recruiter_name.toLowerCase().includes(search) ||
      app.job_role.toLowerCase().includes(search)
    );
  });

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <Loader2 size={40} className="text-slate-200 animate-spin mb-4" />
        <p className="text-slate-400 font-medium">Syncing global applicant pool...</p>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-in fade-in duration-500">
      
      {/* MODALS */}
      {selectedApplicant && (
        <CandidateProfileModal 
          applicant={selectedApplicant} 
          onClose={() => setSelectedApplicant(null)} 
        />
      )}

      {isBulkModalOpen && (
        <BulkMessageModal 
          selectedCount={selectedIds.size} 
          onClose={() => setIsBulkModalOpen(false)} 
          onSend={handleBulkSend} 
        />
      )}

      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
        <div>
          <h1 className="text-3xl font-black text-slate-900 tracking-tight" style={{ fontFamily: "'Inter', system-ui" }}>Global Applicants</h1>
          <p className="text-slate-500 mt-1 text-[15px]">Consolidated view of all candidates across managed recruiters.</p>
        </div>

        <div className="flex items-center gap-4">
          {selectedIds.size > 0 && (
            <button 
              onClick={() => setIsBulkModalOpen(true)}
              className="bg-blue-600 text-white px-6 py-3 rounded-2xl text-sm font-black shadow-lg shadow-blue-600/20 hover:bg-blue-700 transition-all flex items-center gap-2 animate-in slide-in-from-right-4"
            >
              <MessageSquare size={18} />
              Bulk Message ({selectedIds.size})
            </button>
          )}

          <div className="relative group min-w-[300px]">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-slate-900 transition-colors" size={18} />
            <input 
              type="text" 
              placeholder="Search by name, phone, or recruiter..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-white border border-slate-200 rounded-2xl pl-11 pr-5 py-3 text-[14px] font-medium placeholder:text-slate-400 focus:outline-none focus:border-slate-900 focus:ring-4 focus:ring-slate-900/5 transition-all shadow-sm"
            />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-[2rem] border border-slate-200/60 overflow-hidden shadow-xl shadow-slate-200/40">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-100">
                <th className="py-5 px-6 shrink-0 w-12">
                   <div className="flex items-center justify-center">
                    <input 
                      type="checkbox" 
                      className="w-5 h-5 rounded-md border-slate-200 text-slate-900 focus:ring-slate-900 transition-all cursor-pointer" 
                      checked={selectedIds.size === filteredApplicants.length && filteredApplicants.length > 0}
                      onChange={() => {
                        if (selectedIds.size === filteredApplicants.length) {
                          setSelectedIds(new Set());
                        } else {
                          setSelectedIds(new Set(filteredApplicants.map(a => a.id)));
                        }
                      }}
                    />
                   </div>
                </th>
                <th className="py-5 px-6 text-[11px] font-bold uppercase tracking-[0.1em] text-slate-400">Candidate</th>
                <th className="py-5 px-6 text-[11px] font-bold uppercase tracking-[0.1em] text-slate-400">Phone Number</th>
                <th className="py-5 px-6 text-[11px] font-bold uppercase tracking-[0.1em] text-slate-400">Managed Recruiter</th>
                <th className="py-5 px-6 text-[11px] font-bold uppercase tracking-[0.1em] text-slate-400">Job Context</th>
                <th className="py-5 px-6 text-[11px] font-bold uppercase tracking-[0.1em] text-slate-400 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {filteredApplicants.length > 0 ? (
                filteredApplicants.map((app) => (
                  <tr 
                    key={app.id} 
                    className={`transition-colors group cursor-pointer ${selectedIds.has(app.id) ? 'bg-blue-50/50' : 'hover:bg-slate-50/50'}`}
                    onClick={() => setSelectedApplicant(app)}
                  >
                    <td className="py-5 px-6" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-center">
                        <input 
                          type="checkbox" 
                          className="w-5 h-5 rounded-md border-slate-200 text-slate-900 focus:ring-slate-900 transition-all cursor-pointer" 
                          checked={selectedIds.has(app.id)}
                          onChange={() => toggleSelection(app.id)}
                        />
                      </div>
                    </td>
                    <td className="py-5 px-6">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-slate-900 flex items-center justify-center text-white text-xs font-bold shrink-0">
                          {app.candidate?.name?.[0] || '?'}
                        </div>
                        <div>
                          <p className="text-[14px] font-bold text-slate-900">{app.candidate?.name || 'Unknown'}</p>
                          <p className="text-[11px] text-slate-400 flex items-center gap-1">
                            <MapPin size={10} /> {app.candidate?.city || 'No location'}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="py-5 px-6">
                      <a 
                        href={`tel:${app.candidate?.phone}`}
                        onClick={(e) => e.stopPropagation()} 
                        className="group/phone flex items-center gap-2 text-[15px] font-black text-slate-900 hover:text-blue-600 transition-colors"
                      >
                        <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center text-blue-600 group-hover/phone:bg-blue-600 group-hover/phone:text-white transition-all">
                          <PhoneCall size={14} />
                        </div>
                        {app.candidate?.phone}
                      </a>
                    </td>
                    <td className="py-5 px-6">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-lg bg-slate-100 flex items-center justify-center text-slate-500 shrink-0">
                          <Building2 size={12} />
                        </div>
                        <div>
                          <p className="text-[13px] font-semibold text-slate-700">{app.recruiter_name}</p>
                          <a 
                            href={`tel:${app.recruiter_phone}`}
                            onClick={(e) => e.stopPropagation()}
                            className="text-[11px] text-blue-500 font-bold hover:underline"
                          >
                            + Call Recruiter
                          </a>
                        </div>
                      </div>
                    </td>
                    <td className="py-5 px-6">
                      <div>
                        <p className="text-[13px] font-semibold text-slate-800">{app.job_role}</p>
                        <p className="text-[11px] text-slate-400">{app.campaign_title}</p>
                      </div>
                    </td>
                    <td className="py-5 px-6 text-right">
                      <button className="p-2.5 rounded-xl text-slate-400 hover:text-slate-900 hover:bg-slate-100 transition-all">
                        <ExternalLink size={18} />
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className="py-20 text-center">
                    <Users size={40} className="mx-auto text-slate-200 mb-4" />
                    <p className="text-slate-400 font-medium">No applicants found matching your criteria</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
